import { ColumnsType } from 'antd/es/table';
import { useTranslation } from 'react-i18next';
import { InfoCircleOutlined } from '@ant-design/icons';
import { Pagination } from '@/components';
import { ArrowDownRed, ArrowUpGreen } from '@/icons';
import { ICategoryCommissionHistory } from '@/models/commission.model';
import { Table } from '@/ui';
import { formatDate } from '@/utils';
import { getCommissionTrendDirection } from '../../helpers';
import {
  StyledHistoryEmpty,
  StyledHistoryFooterIcon,
  StyledHistoryFooterNote,
  StyledHistoryFooterText,
  StyledHistoryListWrapper,
  StyledTrendValue,
} from '../CommissionList/CommissionListStyles';

type TCommissionHistoryList = {
  history: ICategoryCommissionHistory[];
  isLoading: boolean;
  currentPage: number;
  total: number;
  pageSize: number;
  onPageChange: (page: number) => void;
};

const CommissionHistoryList = ({
  history,
  isLoading,
  currentPage,
  total,
  pageSize,
  onPageChange,
}: TCommissionHistoryList) => {
  const { t } = useTranslation(['commission']);

  const columns: ColumnsType<ICategoryCommissionHistory> = [
    {
      title: t('history_date'),
      dataIndex: 'changed_at',
      key: 'changed_at',
      render: (changedAt: string) => formatDate(changedAt),
    },
    {
      title: t('history_category'),
      dataIndex: ['category', 'name'],
      key: 'category',
      render: (_, record) => record.category?.name ?? '-',
    },
    {
      title: t('history_old_value'),
      dataIndex: 'old_percent',
      key: 'old_percent',
      render: (value: number) => `${value}%`,
    },
    {
      title: t('history_new_value'),
      dataIndex: 'new_percent',
      key: 'new_percent',
      render: (value: number, record) => {
        const direction = getCommissionTrendDirection(record.old_percent, value);

        return (
          <StyledTrendValue $direction={direction}>
            {`${value}%`}
            {direction === 'up' && <ArrowUpGreen />}
            {direction === 'down' && <ArrowDownRed />}
          </StyledTrendValue>
        );
      },
    },
    {
      title: t('history_changed_by'),
      dataIndex: 'changed_by',
      key: 'changed_by',
      render: (changedBy?: ICategoryCommissionHistory['changed_by']) => changedBy?.full_name ?? '-',
    },
  ];

  return (
    <StyledHistoryListWrapper>
      {!isLoading && history.length === 0 ? (
        <StyledHistoryEmpty>{t('history_empty')}</StyledHistoryEmpty>
      ) : (
        <>
          <Table columns={columns} dataSource={history} loading={isLoading} rowKey="id" pagination={false} />
          <Pagination
            hideOnSinglePage
            current={currentPage}
            total={total}
            pageSize={pageSize}
            onChange={onPageChange}
          />
        </>
      )}

      <StyledHistoryFooterNote>
        <StyledHistoryFooterIcon>
          <InfoCircleOutlined />
        </StyledHistoryFooterIcon>
        <StyledHistoryFooterText>{t('history_footer_note')}</StyledHistoryFooterText>
      </StyledHistoryFooterNote>
    </StyledHistoryListWrapper>
  );
};

export default CommissionHistoryList;
